'use client';

import { applyTextStyle } from '@/lib/textStyle';

/**
 * ThankYouBlock — bloc « Merci / Confirmation ».
 *
 * Pensé pour les pages de remerciement (ex. /reception) vers lesquelles le
 * formulaire « visite chantier » redirige après un envoi réussi. Affiche une
 * grande pastille de validation (coche) + un titre + un sous-titre, tous deux
 * entièrement modifiables depuis le page builder (texte, taille, gras, couleur,
 * alignement via <champ>_style).
 *
 * config :
 *   - title        : titre (ex. « Merci ! »)
 *   - subtitle     : sous-titre (ex. « Nous vous recontacterons rapidement. »)
 *   - title_style / subtitle_style : styles par champ (StyledTextField)
 *   - icon_color   : couleur de la pastille (vide = couleur primaire du thème)
 *   - align        : 'center' | 'left'
 */
export default function ThankYouBlock({ config = {} }) {
  const align = config.align === 'left' ? 'left' : 'center';
  const iconColor = (config.icon_color && String(config.icon_color).trim()) || 'var(--color-primary)';
  const title = config.title != null ? config.title : 'Merci !';
  const subtitle = config.subtitle != null ? config.subtitle : 'Nous vous recontacterons rapidement.';

  const titleBase = {
    fontSize: 'clamp(28px, 4.5vw, 44px)',
    fontWeight: 800,
    color: 'var(--color-text)',
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    margin: '0 0 14px',
  };
  const subtitleBase = {
    fontSize: 'clamp(16px, 2vw, 20px)',
    color: 'var(--color-text-muted)',
    lineHeight: 1.6,
    margin: 0,
  };

  return (
    <section style={{ padding: 'clamp(56px, 10vw, 120px) 0', background: 'var(--color-bg, #ffffff)' }}>
      <div
        className="container-page"
        style={{ maxWidth: 680, margin: '0 auto', textAlign: align, display: 'flex', flexDirection: 'column', alignItems: align === 'left' ? 'flex-start' : 'center' }}
      >
        {/* Pastille de validation animée */}
        <div
          style={{
            width: 92,
            height: 92,
            borderRadius: '50%',
            background: iconColor,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 30,
            boxShadow: '0 18px 40px -12px rgba(15, 23, 42, 0.35)',
            animation: 'lbThankPop 0.55s cubic-bezier(0.2, 0.9, 0.3, 1.25) both',
          }}
        >
          <svg
            width="46"
            height="46"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ffffff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ animation: 'lbThankCheck 0.5s ease 0.2s both' }}
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <h1 style={applyTextStyle(config.title_style, titleBase)}>{title}</h1>
        {subtitle ? <p style={applyTextStyle(config.subtitle_style, subtitleBase)}>{subtitle}</p> : null}

        {/* Bouton optionnel, entièrement personnalisable (texte, lien, couleurs). */}
        {config.button_text ? (
          <a
            href={config.button_url || '#'}
            style={{
              display: 'inline-block',
              marginTop: 32,
              padding: '14px 30px',
              borderRadius: 'var(--radius-buttons, 10px)',
              background: (config.button_bg && String(config.button_bg).trim()) || 'var(--color-primary)',
              color: (config.button_text_color && String(config.button_text_color).trim()) || '#ffffff',
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1.2,
              textDecoration: 'none',
              boxShadow: '0 12px 26px -12px rgba(15, 23, 42, 0.4)',
            }}
          >
            {config.button_text}
          </a>
        ) : null}
      </div>

      {/* Animations (pop de la pastille + tracé de la coche). Sans dépendance. */}
      <style
        dangerouslySetInnerHTML={{
          __html:
            '@keyframes lbThankPop{0%{transform:scale(.5);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}' +
            '@keyframes lbThankCheck{0%{stroke-dasharray:0 40;opacity:0}100%{stroke-dasharray:40 40;opacity:1}}',
        }}
      />
    </section>
  );
}
